import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { MeasuredWallpaperData } from '../../Types/ProductPageModel';
import MeasuredWallpaperCuttingsData from '../../Types/MeasuredWallpaperCuttingsData';
import useWindowSize from '../../Hooks/useWindowSize';
import clsx from 'clsx';
import {
  disableBodyScrolling,
  enableBodyScrolling,
} from '../../Utils/scrolling';
import MeasuredWallpaperCutoutArrows from './MeasuredWallpaperCutoutArrows';
import { round } from 'lodash-es';
import MeasuredWallpaperRulers from './MeasuredWallpaperRulers';
import { translate } from '../../Services/translation';
import MeasuredWallpaperOptionButtons from './MeasuredWallpaperOptionButtons';

export interface MeasuredWallpaperCutoutProps {
  measuredWallpaper: MeasuredWallpaperData;
  wallWidth: number;
  wallHeight: number;
  initialCutFromX?: number;
  initialCutFromY?: number;
  initialZoom?: number;
  onCuttingsDataChange?: (
    cuttingData: MeasuredWallpaperCuttingsData
  ) => void;
  maxHeight?: number | null;
  flipHorizontally?: boolean;
  onFlipHorizontally?: (flipHorizontally: boolean) => void;
}

/**
 * Rounds a value up or down to the nearest provided decimal points
 * @param value The value to round
 * @param roundToDecimal Number of decimal points, such as 0.05, 0.01
 * @param roundUp If the value should be rounded up or down. False to round down. Defaults to true.
 * @returns The rounded value
 */
const roundToNearestDecimal = (
  value: number,
  roundToDecimal: number,
  roundUp: boolean = true
) => {
  const multiplier = 1 / roundToDecimal;
  return roundUp
    ? Math.ceil(value * multiplier) / multiplier
    : Math.ceil(value * multiplier) / multiplier;
};

const roundZoomRatio = (value: number) =>
  roundToNearestDecimal(value, 0.05);

const selectionClasses =
  'absolute bottom-0 left-0 right-0 top-0 max-h-full max-w-full';

const MeasuredWallpaperCutout = ({
  measuredWallpaper,
  wallWidth,
  wallHeight,
  initialCutFromX,
  initialCutFromY,
  onCuttingsDataChange,
  onFlipHorizontally,
  initialZoom,
  flipHorizontally = false,
  maxHeight = 768,
}: MeasuredWallpaperCutoutProps) => {
  const patternWidth = measuredWallpaper.patternWidth;
  const patternHeight = measuredWallpaper.patternHeight;
  const breadthWidth = measuredWallpaper.breadthWidth;
  const repeatableX = measuredWallpaper.repeatableX;
  const repeatableY = measuredWallpaper.repeatableY;
  const scalable = measuredWallpaper.scalable;

  const horizontalRepeats = wallWidth / patternWidth;
  const verticalRepeats = wallHeight / patternHeight;
  const totalHorizontalRepeats = Math.max(Math.ceil(horizontalRepeats), 2);

  const hasInitialPosition = useMemo(
    () =>
      typeof initialCutFromX !== 'undefined' &&
      typeof initialCutFromY !== 'undefined',
    [] // do not use any deps, as we don't want to update this variable on change
  );

  const selectionRef = useRef<HTMLDivElement | null>(null);
  const breadthCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const imageRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [windowWidth, windowHeight] = useWindowSize();
  const [borderSize, setBorderSize] = useState<{
    top: number;
    bottom: number;
    x: number;
  }>({ top: 48, bottom: 48, x: 48 });
  const [initialX, setInitialX] = useState(0);
  const [initialY, setInitialY] = useState(0);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [hasDragged, setHasDragged] = useState(false);
  const [showBreadths, setShowBreadths] = useState(false);
  const setImageTranslate = (
    translate: string,
    flipHorizontally: boolean
  ) => {
    if (!imageRef.current) return;
    imageRef.current.style.transform = clsx(
      translate,
      flipHorizontally && 'scaleX(-1)'
    );
  };
  const setImageWidth = (width: number | string) => {
    if (!imageRef.current) return;

    imageRef.current.style.width =
      typeof width === 'number' ? `${width}px` : width;
  };
  const setImageHeight = (height: number) => {
    if (!imageRef.current) return;

    imageRef.current.style.height = `${height}px`;
  };
  const [showRuler, setShowRuler] = useState(false);
  const [showHorizontalLines, setShowHorizontalLines] = useState(false);
  const [showVerticalLines, setShowVerticalLines] = useState(false);
  const [hasSetInitialPosition, setHasSetInitialPosition] = useState(
    !hasInitialPosition
  );

  // For wall heights above pattern height, the wallpaper is actually zoomed, which needs to be calculated
  const calculatedZoomLevel = useMemo(
    () =>
      wallHeight > patternHeight && !repeatableY
        ? round(wallHeight / patternHeight, 6)
        : 1,
    [patternHeight, repeatableY, wallHeight]
  );

  const minZoom = useMemo(
    () =>
      measuredWallpaper.isCustomWallpaper
        ? 1
        : calculateMinZoomLevel(
            repeatableY,
            wallWidth,
            wallHeight,
            patternWidth,
            patternHeight,
            totalHorizontalRepeats,
            measuredWallpaper.minZoomRatio
          ),
    [
      measuredWallpaper.isCustomWallpaper,
      measuredWallpaper.minZoomRatio,
      repeatableY,
      wallWidth,
      wallHeight,
      patternWidth,
      patternHeight,
      totalHorizontalRepeats,
    ]
  );

  // Zoom is from 0, but the initial value is the "visible zoom level"
  const zoom = useRef(initialZoom ?? 0);
  const imageBgSize = useMemo(() => {
    if (!repeatableX && !repeatableY) {
      return `contain`;
    }

    if (wallHeight > patternHeight && repeatableY && !scalable) {
      return `${100 / Math.ceil(horizontalRepeats)}% ${
        100 / verticalRepeats
      }%`;
    }
    return 'contain';
  }, [
    horizontalRepeats,
    patternHeight,
    repeatableX,
    repeatableY,
    scalable,
    verticalRepeats,
    wallHeight,
  ]);

  const bgRepeat = useMemo(() => {
    if (repeatableX && repeatableY) {
      return 'repeat';
    } else if (repeatableX) {
      return 'repeat-x';
    } else if (repeatableY) {
      return 'repeat-y';
    }
    return 'no-repeat';
  }, [repeatableX, repeatableY]);

  const setImagePositionOnDrag = (movingX: number, movingY: number) => {
    if (
      !imageRef?.current ||
      !containerRef?.current ||
      !selectionRef?.current
    )
      return;

    const imageRect = imageRef.current.getBoundingClientRect()!;
    const containerRect = containerRef.current.getBoundingClientRect()!;
    const selectionRect = selectionRef.current.getBoundingClientRect()!;

    const leftLimit =
      selectionRect.left - containerRect.left - borderSize.x;
    const rightLimit = leftLimit + selectionRect.width;
    const rightEdgePos = imageRect.width + movingX;

    // X axis
    if (movingX > leftLimit) {
      movingX = leftLimit;
    }
    if (rightLimit > rightEdgePos) {
      movingX = rightLimit - imageRect.width;
    }

    // Y axis
    const topLimit =
      selectionRect.top - containerRect.top - borderSize.top;
    const bottomLimit = topLimit + selectionRect.height;
    const bottomEdgePos = imageRect.height + movingY;
    if (movingY > topLimit) {
      movingY = topLimit;
    }
    if (bottomLimit > bottomEdgePos) {
      movingY = bottomLimit - imageRect.height;
    }

    setOffsetX(movingX);
    setOffsetY(movingY);

    setImageTranslate(
      `translate3d(${movingX}px, ${movingY}px, 0)`,
      flipHorizontally
    );
  };

  const onDragStart = (
    e:
      | React.TouchEvent<HTMLElement>
      | React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    setHasDragged(true);
    if (e.type == 'touchstart') {
      const touchEvent = e as React.TouchEvent<HTMLElement>;
      var touchPos = touchEvent.touches[0];
      if (!touchPos) return;
      setInitialX(touchPos.clientX - offsetX);
      setInitialY(touchPos.clientY - offsetY);
    } else {
      const mouseEvent = e as React.MouseEvent<HTMLElement, MouseEvent>;
      if (mouseEvent.button != 0) return; // Only move on left click

      setInitialX(mouseEvent.clientX - offsetX);
      setInitialY(mouseEvent.clientY - offsetY);
    }

    if (e.target == imageRef.current) {
      setIsDragging(true);
      disableBodyScrolling(true);
      document.body.style.userSelect = 'none'; // Disable selecting text when moving the image
    }
  };

  const onDrag = useCallback(
    (
      e:
        | React.TouchEvent<HTMLElement>
        | React.MouseEvent<HTMLElement, MouseEvent>
    ) => {
      if (!isDragging) return;
      e.stopPropagation();
      let movingX = 0;
      let movingY = 0;

      if (e.type == 'touchmove') {
        const touchEvent = e as React.TouchEvent<HTMLElement>;
        var touchPos = touchEvent.touches[0];
        if (!touchPos) return;
        movingX = touchPos.clientX - initialX;
        movingY = touchPos.clientY - initialY;
      } else {
        const mouseEvent = e as React.MouseEvent<HTMLElement, MouseEvent>;
        movingX = mouseEvent.clientX - initialX;

        movingY = mouseEvent.clientY - initialY;
      }

      setImagePositionOnDrag(movingX, movingY);
    },
    [initialX, initialY, isDragging]
  );

  const onDragEnd = useCallback(() => {
    setInitialX(0);
    setInitialY(0);

    setIsDragging(false);
    document.body.style.removeProperty('user-select'); // Re-enable selecting text
    enableBodyScrolling();
  }, []);

  /* Set image position to bottom left */
  const resetImagePositionToBottomLeft = () => {
    if (
      !containerRef.current ||
      !imageRef.current ||
      !selectionRef.current ||
      hasInitialPosition
    )
      return;

    const imageRect = imageRef.current?.getBoundingClientRect()!;
    const containerRect = containerRef.current?.getBoundingClientRect()!;
    const selectionRect = selectionRef.current?.getBoundingClientRect()!;
    const topLimit = selectionRef.current.offsetTop!;
    const bottomLimit = topLimit + selectionRect.height;

    const posX = selectionRect.left - containerRect.left - borderSize.x;
    const posY = bottomLimit - imageRect.height;

    setImagePositionOnDrag(posX, posY);
    if (wallWidth > 0 && wallHeight > 0) {
      setHasDragged(true);
    }
  };

  const getZoomRatio = () => {
    const calculatedZoom = Math.max(
      1,
      round(wallHeight / patternHeight, 6)
    );
    return repeatableY ? 1 : calculatedZoom + zoom.current;
  };

  const getScalableCanvasRatio = () => {
    const wallRatio = wallWidth / wallHeight;
    const imageRatio = patternWidth / patternHeight;
    const isWidthMaxed = imageRatio < wallRatio;
    return isWidthMaxed
      ? wallWidth / patternWidth
      : wallHeight / patternHeight;
  };

  const getCanvasWidth = () => {
    const zoomRatio = getZoomRatio();

    if (scalable) {
      const canvasRatio = getScalableCanvasRatio();
      return round(canvasRatio * patternWidth * zoomRatio, 2);
    }

    let patternRepeatsX = totalHorizontalRepeats;

    if (
      repeatableY &&
      repeatableX &&
      wallWidth >= patternWidth * patternRepeatsX
    ) {
      patternRepeatsX += 1;
    }

    return patternWidth * patternRepeatsX * zoomRatio;
  };

  const getCanvasHeight = () => {
    const zoomRatio = getZoomRatio();

    if (scalable) {
      const canvasRatio = getScalableCanvasRatio();
      return round(canvasRatio * patternHeight * zoomRatio, 2);
    }

    if (patternHeight >= wallHeight) {
      return patternHeight * zoomRatio;
    }

    return wallHeight * (1 + zoom.current);
  };

  const getCuttingsData =
    useCallback((): MeasuredWallpaperCuttingsData => {
      const selectionRect = selectionRef.current?.getBoundingClientRect();
      const imageRect = imageRef.current?.getBoundingClientRect();
      if (!selectionRect || !imageRect)
        return {
          cutFromX: 0,
          cutToX: 0,
          cutFromY: 0,
          cutToY: 0,
          zoom: zoom.current,
          mirrored: flipHorizontally,
        };

      const x = selectionRect.left - imageRect.left;
      const y = selectionRect.top - imageRect.top;

      const canvasWidth = getCanvasWidth();
      const canvasHeight = getCanvasHeight();

      const cutFromX = x / imageRect.width;

      const cutSizeX = wallWidth / canvasWidth;
      const cutToX = cutFromX + cutSizeX;

      const cutFromY = y / imageRect.height;
      const cutSizeY = wallHeight / canvasHeight;
      const cutToY = cutFromY + cutSizeY;

      return {
        cutFromX: round(Math.max(0, cutFromX), 6).toFixed(6),
        cutToX: round(Math.min(1, cutToX), 6).toFixed(6),

        cutFromY: round(Math.max(0, cutFromY), 6).toFixed(6),
        cutToY: round(Math.min(1, cutToY), 6).toFixed(6),

        zoom: zoom.current,
        mirrored: flipHorizontally,
      };
    }, [flipHorizontally, zoom, wallWidth, wallHeight]);

  const setInitialImagePosition = (cutFromX: number, cutFromY: number) => {
    const selectionRect = selectionRef.current!.getBoundingClientRect();
    const imageRect = imageRef.current!.getBoundingClientRect();

    const initialX = selectionRect.left - imageRect.left;
    const initialY = selectionRect.top - imageRect.top;

    const posX = initialX - cutFromX * imageRect.width;
    const posY = initialY - cutFromY * imageRect.height;

    setImagePositionOnDrag(posX, posY);
    setHasSetInitialPosition(true);
  };

  const drawBreadths = useCallback(
    (ctx) => {
      ctx.lineWidth = 2;
      ctx.strokeStyle = 'hotpink';

      if (window.innerWidth < 1024 || wallWidth > 1000) {
        ctx.lineWidth = 1;
      }
      const selectionWidth =
        selectionRef.current?.getBoundingClientRect().width ?? 0;

      const cmToPx = selectionWidth / wallWidth;

      const num = wallWidth / breadthWidth;
      const strokeGap = breadthWidth * cmToPx;

      if (showBreadths) {
        for (let j = 0, i = strokeGap; j < num - 1; j++) {
          ctx.moveTo(i, 0);
          ctx.lineTo(i, 1000);
          ctx.stroke();
          i += strokeGap;
        }
      }
    },
    [breadthWidth, wallWidth, showBreadths]
  );

  const onZoom = (newZoomLevel: number) => {
    zoom.current = newZoomLevel;
    calculateImageSizeAndSetPosition();
  };

  /* Draw/hide breadths on canvas */
  useLayoutEffect(() => {
    const canvas = breadthCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.canvas.width = ctx.canvas.offsetWidth;
    drawBreadths(ctx);
  }, [
    showBreadths,
    windowWidth,
    borderSize,
    drawBreadths,
    wallWidth,
    wallHeight,
  ]);

  /* Allow moving wallpaper when moving mouse outside container */
  useLayoutEffect(() => {
    const mouseMove = (e: any) => onDrag(e);
    const mouseUp = () => onDragEnd();

    document.addEventListener('mousemove', mouseMove);
    document.addEventListener('mouseup', mouseUp);

    return () => {
      document.removeEventListener('mousemove', mouseMove);
      document.removeEventListener('mouseup', mouseUp);
    };
  }, [onDrag, onDragEnd]);

  const setScalableImageSizes = (
    selectHeight: number,
    zoomRatio: number
  ) => {
    let imageWidthPercent: number | undefined;
    let imageHeight = selectHeight;
    const wallRatio = wallWidth / wallHeight;
    const imageRatio = patternWidth / patternHeight;
    const isWidthMaxed = imageRatio < wallRatio;

    if (isWidthMaxed) {
      // If the selected wall size is maxed in width, calculate the height and unset image width to make it maxed out
      imageHeight = (selectHeight * wallRatio) / imageRatio;
      imageWidthPercent = undefined;
    } else {
      // Otherwise, height is maxed, unset width percent to use aspect-ratio on the image element
      imageWidthPercent = undefined;
    }

    setImageWidth(
      typeof imageWidthPercent === 'undefined'
        ? ''
        : imageWidthPercent * zoomRatio + '%'
    );
    setImageHeight(imageHeight * zoomRatio);
  };

  const setImagePositionByCuttingsData = (
    cutFromX: number,
    cutFromY: number
  ) => {
    if (
      isNaN(cutFromX) ||
      !isFinite(cutFromX) ||
      isNaN(cutFromY) ||
      !isFinite(cutFromY) ||
      !selectionRef.current ||
      !imageRef.current
    )
      return;

    const imageRect = imageRef.current.getBoundingClientRect();

    const posX =
      selectionRef.current.offsetLeft! - cutFromX * imageRect.width;
    const posY =
      selectionRef.current.offsetTop! - cutFromY * imageRect.height;

    setImagePositionOnDrag(posX, posY);
  };

  const calculateImageSizeAndSetPosition = () => {
    if (!selectionRef.current || !imageRef.current) return;

    const cuttingsData = getCuttingsData();
    const selectionRect = selectionRef.current.getBoundingClientRect();
    const selectHeight = selectionRect.height;
    const zoomRatio = getZoomRatio();
    const canvasWidth = getCanvasWidth();
    const canvasHeight = getCanvasHeight();
    if (scalable) {
      setScalableImageSizes(selectHeight, zoomRatio);
      if (zoomRatio == 1) {
        resetImagePositionToBottomLeft();
      }
    } else {
      const canvasRatio = canvasWidth / canvasHeight;
      const heightRatio = wallHeight / canvasHeight;
      const newImageHeight = selectHeight / heightRatio;
      const newImageWidth = canvasRatio * newImageHeight;

      setImageWidth(newImageWidth);
      setImageHeight(newImageHeight);
    }

    if (hasInitialPosition && !hasSetInitialPosition) {
      setInitialImagePosition(initialCutFromX!, initialCutFromY!);
    } else if (!hasInitialPosition && !hasDragged) {
      resetImagePositionToBottomLeft();
    } else if (minZoom > zoomRatio) {
      zoom.current = 0;
      resetImagePositionToBottomLeft(); // Timeout needed to let the document actually update
    } else {
      const cutFromX =
        typeof cuttingsData.cutFromX === 'number'
          ? cuttingsData.cutFromX
          : parseFloat(cuttingsData.cutFromX);
      const cutFromY =
        typeof cuttingsData.cutFromY === 'number'
          ? cuttingsData.cutFromY
          : parseFloat(cuttingsData.cutFromY);
      setImagePositionByCuttingsData(cutFromX, cutFromY);
    }
  };

  /* Trigger onCuttingsDataChange when moving the image */
  useLayoutEffect(() => {
    if (!imageRef.current || typeof onCuttingsDataChange !== 'function')
      return;

    const observer = new MutationObserver(() => {
      const data = getCuttingsData();
      onCuttingsDataChange(data);
    });

    observer.observe(imageRef.current, {
      attributes: true,
      childList: false,
      characterData: false,
      subtree: false,
      attributeFilter: ['style'],
    });

    return () => {
      observer.disconnect();
    };
  }, [imageRef, onCuttingsDataChange, getCuttingsData]);

  useLayoutEffect(() => {
    const cuttingsData = getCuttingsData();
    if (onCuttingsDataChange) {
      onCuttingsDataChange(cuttingsData);
    }
  }, [flipHorizontally]);

  // Reset zoom when changing wall height
  useLayoutEffect(() => {
    if (zoom.current !== 0 && (hasDragged || hasSetInitialPosition)) {
      onZoom(0);
    }
  }, [wallHeight]);

  useLayoutEffect(() => {
    if (!selectionRef.current || !imageRef.current) return;
    // Main effect that triggers calculation of image size then repositions it
    calculateImageSizeAndSetPosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [borderSize, windowWidth, wallWidth, wallHeight, flipHorizontally]);

  const hasAnyOptionToggled = useMemo(
    () =>
      showRuler ||
      showBreadths ||
      showHorizontalLines ||
      showVerticalLines ||
      flipHorizontally,
    [
      flipHorizontally,
      showBreadths,
      showHorizontalLines,
      showRuler,
      showVerticalLines,
    ]
  );

  return (
    <div className="relative top-4 flex h-full flex-col lg:sticky lg:col-span-2 lg:h-[600px] lg:max-h-[600px] xl:h-[768px] xl:max-h-[768px]">
      <div className="relative w-full flex-1 border-gray-100 bg-gray-100 lg:rounded-t-lg lg:border-x-2 lg:border-t-2 md-down:border-t">
        <div
          className="grid h-full min-h-[calc(100vh_*_0.5)] w-full overflow-hidden"
          onTouchEnd={onDragEnd}
          onMouseUp={onDragEnd}
          ref={containerRef}
          id="measured-wallpaper-cutout"
        >
          <div
            id="outer"
            className="relative h-full w-full"
            style={{
              maxHeight: maxHeight ? `${maxHeight}px` : undefined,
              borderTopWidth: borderSize.top,
              borderBottomWidth: borderSize.bottom,
              borderLeftWidth: borderSize.x,
              borderRightWidth: borderSize.x,
              borderStyle: 'solid',
              borderColor: 'transparent',
            }}
          >
            <div
              className="absolute bg-left-bottom"
              ref={imageRef}
              style={{
                transform: 'translate3d(0, 0, 0)',
                aspectRatio: scalable
                  ? `${patternWidth} / ${patternHeight}`
                  : undefined,
                backgroundImage: `url(${measuredWallpaper.imageUrl})`,
                backgroundSize: imageBgSize,
                backgroundRepeat: bgRepeat,
              }}
              onMouseDown={onDragStart}
              onMouseUp={onDragEnd}
              onMouseMove={onDrag}
              onTouchStart={onDragStart}
              onTouchMove={onDrag}
              onTouchEnd={onDragEnd}
            ></div>
            <div
              ref={selectionRef}
              id="selection"
              className={clsx(
                selectionClasses,
                'pointer-events-none z-10 m-auto before:absolute before:h-full before:w-full before:border before:border-solid before:border-gray-900 before:outline before:outline-[100vh] before:outline-[rgba(255,255,255,0.7)]'
              )}
              style={{
                aspectRatio: `${wallWidth / wallHeight}`,
              }}
            >
              <canvas
                className="pointer-events-none absolute z-10 h-full w-full"
                ref={breadthCanvasRef}
              ></canvas>
              <canvas
                className="pointer-events-none absolute z-10 h-full w-full"
                id="rulers-canvas"
              ></canvas>
              <canvas
                id="bottom-rulers-canvas"
                className="absolute w-full"
              ></canvas>
              <canvas
                id="right-rulers-canvas"
                className="absolute h-full"
              ></canvas>
              <MeasuredWallpaperRulers
                showRuler={showRuler}
                showVerticalLines={showVerticalLines}
                showHorizontalLines={showHorizontalLines}
                selectionRef={selectionRef}
                measuredWallpaper={measuredWallpaper}
                wallWidth={wallWidth}
                wallHeight={wallHeight}
              />
              <MeasuredWallpaperCutoutArrows
                wallWidth={wallWidth}
                wallHeight={wallHeight}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="z-10 w-full border-2 border-solid border-gray-100 bg-[#fbfbfb] lg:rounded-b-lg">
        <MeasuredWallpaperOptionButtons
          showNotificationDot={hasAnyOptionToggled}
          buttons={[
            {
              icon: showRuler ? 'ruler-fill' : 'ruler',
              onClick: () => setShowRuler(!showRuler),
              tooltip: translate(
                showRuler
                  ? 'measured-wallpaper.hide-ruler'
                  : 'measured-wallpaper.show-ruler'
              ),
            },
            {
              icon: showVerticalLines
                ? 'square-split-horizontal-fill'
                : 'square-split-horizontal',
              onClick: () => setShowVerticalLines(!showVerticalLines),
              tooltip: translate(
                showVerticalLines
                  ? 'measured-wallpaper.hide-vertical-lines'
                  : 'measured-wallpaper.show-vertical-lines'
              ),
            },
            {
              icon: showHorizontalLines
                ? 'square-split-vertical-fill'
                : 'square-split-vertical',
              onClick: () => setShowHorizontalLines(!showHorizontalLines),
              tooltip: translate(
                showHorizontalLines
                  ? 'measured-wallpaper.hide-horizontal-lines'
                  : 'measured-wallpaper.show-horizontal-lines'
              ),
            },
            {
              icon: showBreadths ? 'columns-fill' : 'columns',
              onClick: () => setShowBreadths(!showBreadths),
              tooltip: translate(
                showBreadths
                  ? 'measured-wallpaper.hide-breadths'
                  : 'measured-wallpaper.show-breadths'
              ),
            },
            {
              icon: flipHorizontally ? 'perspective-fill' : 'perspective',
              onClick: () =>
                onFlipHorizontally &&
                onFlipHorizontally(!flipHorizontally),
              tooltip: translate(
                flipHorizontally
                  ? 'measured-wallpaper.reset-flip'
                  : 'measured-wallpaper.flip-horizontally'
              ),
            },
          ]}
          minZoomRatio={minZoom}
          maxZoomRatio={measuredWallpaper.maxZoomRatio}
          zoomRatioStep={measuredWallpaper.zoomRatioSteps}
          currentZoomRatio={zoom.current}
          visibleZoomLevel={
            repeatableY
              ? 1
              : roundZoomRatio(calculatedZoomLevel + zoom.current)
          }
          onZoom={onZoom}
          showZoomButtons={!repeatableY}
        />
      </div>
    </div>
  );
};

function calculateMinZoomLevel(
  repeatableY: boolean,
  wallWidth: number,
  wallHeight: number,
  patternWidth: number,
  patternHeight: number,
  totalHorizontalRpeats: number,
  minZoomRatio: number
) {
  if (repeatableY) return 1;

  return Math.max(
    roundZoomRatio(wallHeight / patternHeight),
    roundZoomRatio(wallWidth / (patternWidth * totalHorizontalRpeats)),
    minZoomRatio
  );
}

export default MeasuredWallpaperCutout;
